import { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Save, Plus, Trash2, ToggleLeft, ToggleRight, ConstructionIcon, Globe, Youtube, Instagram, Facebook, Linkedin, Twitter } from "lucide-react";

const TABS = [
  { v: "features", l: "Feature Flags" },
  { v: "pages", l: "Pages" },
  { v: "score", l: "Score Engine" },
  { v: "countries", l: "Countries" },
  { v: "social", l: "Social Links" },
];

export default function AdminPlatform() {
  const [tab, setTab] = useState("features");
  return (
    <AdminLayout subtitle="Modules 14·15·17·18·19" title="Platform Configuration">
      <div className="flex flex-wrap gap-px bg-white/10 mb-6" data-testid="platform-tabs">
        {TABS.map((t) => (
          <button
            key={t.v}
            onClick={() => setTab(t.v)}
            className={`flex-1 min-w-[140px] px-4 py-3 text-sm bg-ec-ink transition-colors ${tab === t.v ? "border-b-2 border-ec-emerald text-white font-bold" : "text-zinc-400 hover:bg-white/5"}`}
            data-testid={`platform-tab-${t.v}`}
          >{t.l}</button>
        ))}
      </div>
      {tab === "features" && <FeaturesTab />}
      {tab === "pages" && <PagesTab />}
      {tab === "score" && <ScoreEngineTab />}
      {tab === "countries" && <CountriesTab />}
      {tab === "social" && <SocialTab />}
    </AdminLayout>
  );
}

// ---------- Features ----------
function FeaturesTab() {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => { try { setRows((await api.get("/admin/feature-flags")).data.rows); } catch (e) { console.error("load feature-flags failed", e); } }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (key, enabled) => {
    try { await api.patch(`/admin/feature-flags/${key}`, { enabled }); load(); toast.success(`${key} ${enabled ? "enabled" : "disabled"}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const grouped = rows.reduce((acc, r) => { (acc[r.category] = acc[r.category] || []).push(r); return acc; }, {});
  return (
    <div className="space-y-6" data-testid="features-tab">
      {Object.entries(grouped).map(([cat, items]) => (
        <Card key={cat} className="rounded-sm border-white/10 bg-white/[0.02]">
          <CardContent className="p-6">
            <div className="eyebrow text-ec-emerald mb-3">{cat}</div>
            <ul className="divide-y divide-white/5">
              {items.map((f) => (
                <li key={f.key} className="flex items-center justify-between py-3" data-testid={`feature-${f.key}`}>
                  <div>
                    <div className="font-semibold text-white">{f.label}</div>
                    <div className="text-[10px] font-mono-plex text-zinc-500">{f.key}</div>
                  </div>
                  <Switch checked={!!f.enabled} onCheckedChange={(v) => toggle(f.key, v)} data-testid={`feature-toggle-${f.key}`} />
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ---------- Pages ----------
function PagesTab() {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => { try { setRows((await api.get("/admin/page-flags")).data.rows); } catch (e) { console.error("load page-flags failed", e); } }, []);
  useEffect(() => { load(); }, [load]);
  const update = async (slug, body) => {
    try { await api.patch(`/admin/page-flags/${slug}`, body); load(); toast.success(`${slug} updated`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Card className="rounded-sm border-white/10 bg-white/[0.02]" data-testid="pages-tab">
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5">
            <tr className="text-left">
              <th className="px-4 py-3 eyebrow text-zinc-500">Page</th>
              <th className="px-4 py-3 eyebrow text-zinc-500">Slug</th>
              <th className="px-4 py-3 eyebrow text-zinc-500">Enabled</th>
              <th className="px-4 py-3 eyebrow text-zinc-500">Maintenance</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.slug} className="border-t border-white/5" data-testid={`page-${p.slug}`}>
                <td className="px-4 py-3 text-white">{p.label}</td>
                <td className="px-4 py-3 font-mono-plex text-xs text-zinc-400">{p.slug}</td>
                <td className="px-4 py-3"><Switch checked={!!p.enabled} onCheckedChange={(v) => update(p.slug, { enabled: v })} data-testid={`page-toggle-enabled-${p.slug}`} /></td>
                <td className="px-4 py-3"><Switch checked={!!p.maintenance} onCheckedChange={(v) => update(p.slug, { maintenance: v })} data-testid={`page-toggle-maintenance-${p.slug}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

// ---------- Score Engine ----------
function ScoreEngineTab() {
  const [doc, setDoc] = useState(null);
  const load = useCallback(async () => { try { setDoc((await api.get("/admin/score-engine")).data); } catch (e) { console.error("load score-engine failed", e); } }, []);
  useEffect(() => { load(); }, [load]);
  if (!doc) return <div className="text-zinc-500 text-sm">Loading…</div>;
  const totalWeight = Object.values(doc.weights || {}).reduce((a, b) => a + (Number(b) || 0), 0);
  const setWeight = (k, v) => setDoc({ ...doc, weights: { ...doc.weights, [k]: Number(v) } });
  const setTier = (i, k, v) => {
    const nt = [...(doc.tiers || [])];
    nt[i] = { ...nt[i], [k]: k === "key" || k === "color" ? v : Number(v) };
    setDoc({ ...doc, tiers: nt });
  };
  const save = async () => {
    try { await api.put("/admin/score-engine", { weights: doc.weights, tiers: doc.tiers }); toast.success("Score engine saved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="score-tab">
      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl font-bold text-white">Category weights</h3>
            <Badge className="bg-white/10 border-white/10 rounded-sm font-mono-plex">Σ {totalWeight}</Badge>
          </div>
          <div className="space-y-3" data-testid="score-weights">
            {Object.entries(doc.weights).map(([k, v]) => (
              <div key={k} className="flex items-center gap-3">
                <Label className="eyebrow text-zinc-500 w-32 capitalize">{k}</Label>
                <Input type="number" value={v} onChange={(e) => setWeight(k, e.target.value)} className="rounded-sm bg-white/5 border-white/10 text-white max-w-[120px]" data-testid={`score-weight-${k}`} />
                <div className="flex-1 h-2 bg-white/5 rounded-sm overflow-hidden">
                  <div className="h-full bg-ec-emerald" style={{ width: `${(Number(v) / Math.max(1, totalWeight)) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-6">
          <h3 className="font-display text-xl font-bold text-white mb-4">Trust tiers (0 → 1000)</h3>
          <div className="space-y-2" data-testid="trust-tiers">
            {(doc.tiers || []).map((t, i) => (
              <div key={t.key || `tier-${i}`} className="grid grid-cols-[140px_70px_70px_50px] gap-2 items-center" data-testid={`tier-row-${i}`}>
                <Input value={t.key} onChange={(e) => setTier(i, "key", e.target.value)} className="rounded-sm bg-white/5 border-white/10 text-white" data-testid={`tier-key-${i}`} />
                <Input type="number" value={t.min} onChange={(e) => setTier(i, "min", e.target.value)} className="rounded-sm bg-white/5 border-white/10 text-white" data-testid={`tier-min-${i}`} />
                <Input type="number" value={t.max} onChange={(e) => setTier(i, "max", e.target.value)} className="rounded-sm bg-white/5 border-white/10 text-white" data-testid={`tier-max-${i}`} />
                <input type="color" value={t.color || "#22c55e"} onChange={(e) => setTier(i, "color", e.target.value)} className="h-10 w-full rounded-sm border border-white/10 bg-transparent cursor-pointer" data-testid={`tier-color-${i}`} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="lg:col-span-2 flex justify-end">
        <Button onClick={save} className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink gap-2" data-testid="score-save"><Save className="w-4 h-4" />Save score engine</Button>
      </div>
    </div>
  );
}

// ---------- Countries ----------
function CountriesTab() {
  const [rows, setRows] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [edit, setEdit] = useState(null);
  const load = useCallback(async () => { try { setRows((await api.get("/admin/countries")).data.rows); } catch (e) { console.error("load countries failed", e); } }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (code, enabled) => {
    const row = rows.find((r) => r.code === code);
    try { await api.patch(`/admin/countries/${code}`, { ...row, enabled }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (code) => {
    if (!confirm(`Delete country ${code}?`)) return;
    try { await api.delete(`/admin/countries/${code}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="countries-tab">
      <div className="flex justify-end mb-3">
        <Button onClick={() => setShowNew(true)} className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink gap-2" data-testid="country-new"><Plus className="w-4 h-4" />Add country</Button>
      </div>
      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="countries-table">
            <thead className="bg-white/5">
              <tr className="text-left">
                <th className="px-4 py-3 eyebrow text-zinc-500">Code</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Name</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Currency</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Tax %</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Phone</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Enabled</th>
                <th className="px-4 py-3 eyebrow text-zinc-500 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.code} className="border-t border-white/5" data-testid={`country-${c.code}`}>
                  <td className="px-4 py-3 font-mono-plex font-bold text-white">{c.code}</td>
                  <td className="px-4 py-3 text-white">{c.name}</td>
                  <td className="px-4 py-3 font-mono-plex text-zinc-300">{c.currency}</td>
                  <td className="px-4 py-3 font-mono-plex text-zinc-300">{c.tax_percent}%</td>
                  <td className="px-4 py-3 font-mono-plex text-xs text-zinc-400">{c.phone_country_code} · {c.phone_format}</td>
                  <td className="px-4 py-3"><Switch checked={!!c.enabled} onCheckedChange={(v) => toggle(c.code, v)} data-testid={`country-toggle-${c.code}`} /></td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setEdit(c)} className="px-2 py-1 text-xs border border-white/10 rounded-sm text-white hover:bg-white/10 mr-1" data-testid={`country-edit-${c.code}`}>Edit</button>
                    <button onClick={() => del(c.code)} className="p-1.5 border border-ec-error/30 text-ec-error rounded-sm hover:bg-ec-error/10" data-testid={`country-delete-${c.code}`}><Trash2 className="w-3 h-3" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {(showNew || edit) && <CountryModal initial={edit} isNew={showNew && !edit} onClose={() => { setShowNew(false); setEdit(null); }} onSaved={() => { setShowNew(false); setEdit(null); load(); }} />}
    </div>
  );
}

const EMPTY_COUNTRY = { code: "", name: "", enabled: true, currency: "USD", tax_percent: 0, phone_country_code: "+1", phone_format: "##########", date_format: "DD/MM/YYYY" };

function CountryModal({ initial, isNew, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY_COUNTRY, ...initial });
  const save = async () => {
    try {
      const payload = { ...f, tax_percent: Number(f.tax_percent) };
      if (isNew) await api.post("/admin/countries", payload);
      else await api.patch(`/admin/countries/${initial.code}`, payload);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={onClose} data-testid="country-modal">
      <div className="bg-ec-ink border border-white/10 rounded-sm max-w-xl w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-xl font-bold text-white mb-4">{isNew ? "New country" : `Edit ${f.code}`}</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            ["code", "Code (ISO)"], ["name", "Name"], ["currency", "Currency"], ["tax_percent", "Tax %"],
            ["phone_country_code", "Phone +CC"], ["phone_format", "Phone format"], ["date_format", "Date format"],
          ].map(([k, l]) => (
            <div key={k}>
              <Label className="eyebrow text-zinc-500">{l}</Label>
              <Input value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="rounded-sm bg-white/5 border-white/10 text-white mt-2" disabled={!isNew && k === "code"} data-testid={`country-input-${k}`} />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" className="rounded-sm border-white/10 text-white" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="rounded-sm bg-ec-emerald text-ec-ink" data-testid="country-save">Save</Button>
        </div>
      </div>
    </div>
  );
}


// ---------- Social Links ----------
const SOCIAL_ICONS = {
  youtube: Youtube,
  instagram: Instagram,
  facebook: Facebook,
  linkedin: Linkedin,
  twitter: Twitter,
};

function SocialTab() {
  const [rows, setRows] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(async () => {
    try { setRows((await api.get("/admin/social-links")).data.rows); }
    catch (e) { console.error("load social-links failed", e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (key, body) => {
    try { await api.patch(`/admin/social-links/${key}`, body); load(); toast.success(`${key} updated`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (key) => {
    if (!confirm(`Delete social link "${key}"?`)) return;
    try { await api.delete(`/admin/social-links/${key}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="social-tab">
      <div className="flex justify-end mb-3">
        <Button onClick={() => setShowNew(true)} className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink gap-2" data-testid="social-new">
          <Plus className="w-4 h-4" />Add social link
        </Button>
      </div>
      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="social-table">
            <thead className="bg-white/5">
              <tr className="text-left">
                <th className="px-4 py-3 eyebrow text-zinc-500">Platform</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Label</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">URL</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Order</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Enabled</th>
                <th className="px-4 py-3 eyebrow text-zinc-500 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => <SocialRow key={s.key} row={s} onPatch={patch} onDelete={del} />)}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-zinc-500 italic text-xs">No social links yet.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {showNew && <SocialModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function SocialRow({ row, onPatch, onDelete }) {
  const [url, setUrl] = useState(row.url || "");
  const [label, setLabel] = useState(row.label || "");
  const [order, setOrder] = useState(row.order ?? 0);
  const Icon = SOCIAL_ICONS[row.key] || Globe;
  return (
    <tr className="border-t border-white/5" data-testid={`social-row-${row.key}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2 text-white">
          <Icon className="w-4 h-4 text-ec-emerald" />
          <span className="font-mono-plex text-xs uppercase">{row.key}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={() => label !== row.label && onPatch(row.key, { label })}
          className="rounded-sm bg-white/5 border-white/10 text-white h-8 text-xs"
          data-testid={`social-label-${row.key}`}
        />
      </td>
      <td className="px-4 py-3">
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onBlur={() => url !== row.url && onPatch(row.key, { url })}
          placeholder="https://…"
          className="rounded-sm bg-white/5 border-white/10 text-white h-8 text-xs font-mono-plex min-w-[220px]"
          data-testid={`social-url-${row.key}`}
        />
      </td>
      <td className="px-4 py-3">
        <Input
          type="number"
          value={order}
          onChange={(e) => setOrder(Number(e.target.value))}
          onBlur={() => order !== (row.order ?? 0) && onPatch(row.key, { order: Number(order) })}
          className="rounded-sm bg-white/5 border-white/10 text-white h-8 text-xs w-20"
          data-testid={`social-order-${row.key}`}
        />
      </td>
      <td className="px-4 py-3">
        <Switch
          checked={!!row.enabled}
          onCheckedChange={(v) => onPatch(row.key, { enabled: v })}
          data-testid={`social-toggle-${row.key}`}
        />
      </td>
      <td className="px-4 py-3 text-right">
        <button
          onClick={() => onDelete(row.key)}
          className="p-1.5 border border-ec-error/30 text-ec-error rounded-sm hover:bg-ec-error/10"
          data-testid={`social-delete-${row.key}`}
        ><Trash2 className="w-3 h-3" /></button>
      </td>
    </tr>
  );
}

function SocialModal({ onClose, onSaved }) {
  const [f, setF] = useState({ key: "", label: "", url: "", enabled: true, order: 99 });
  const save = async () => {
    try {
      await api.post("/admin/social-links", { ...f, order: Number(f.order) });
      toast.success("Social link added");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={onClose} data-testid="social-modal">
      <div className="bg-ec-ink border border-white/10 rounded-sm max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-xl font-bold text-white mb-4">New social link</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-1">
            <Label className="eyebrow text-zinc-500">Key (lowercase, unique)</Label>
            <Input value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })} placeholder="tiktok" className="rounded-sm bg-white/5 border-white/10 text-white mt-2" data-testid="social-input-key" />
          </div>
          <div className="col-span-1">
            <Label className="eyebrow text-zinc-500">Label</Label>
            <Input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="TikTok" className="rounded-sm bg-white/5 border-white/10 text-white mt-2" data-testid="social-input-label" />
          </div>
          <div className="col-span-2">
            <Label className="eyebrow text-zinc-500">URL</Label>
            <Input value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://…" className="rounded-sm bg-white/5 border-white/10 text-white mt-2 font-mono-plex" data-testid="social-input-url" />
          </div>
          <div className="col-span-1">
            <Label className="eyebrow text-zinc-500">Order</Label>
            <Input type="number" value={f.order} onChange={(e) => setF({ ...f, order: e.target.value })} className="rounded-sm bg-white/5 border-white/10 text-white mt-2" data-testid="social-input-order" />
          </div>
          <div className="col-span-1 flex items-end gap-2 pb-1">
            <Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="social-input-enabled" />
            <Label className="text-xs text-zinc-400">Enabled</Label>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" className="rounded-sm border-white/10 text-white" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="rounded-sm bg-ec-emerald text-ec-ink" data-testid="social-save">Save</Button>
        </div>
      </div>
    </div>
  );
}
